import React, { useMemo } from "react";
import { useMeeting } from "@videosdk.live/react-sdk";
import { useBreakoutRooms } from "../../context/BreakoutRoomsContext";

export default function BreakoutRoomsParticipantPanel({ isOpen, onClose }) {
  const mMeeting = useMeeting();
  const localParticipantId = mMeeting?.localParticipant?.id;

  const {
    rooms,
    currentRoom,
    requestJoinRoom,
    leaveRoom,
    joinRequests,
  } = useBreakoutRooms();

  const roomList = useMemo(() => Object.values(rooms), [rooms]);

  // Check if local participant has pending requests
  const pendingRequests = useMemo(() => {
    const requests = [];
    Object.entries(joinRequests).forEach(([roomId, roomRequests]) => {
      const hasRequest = roomRequests.some((req) => req.participantId === localParticipantId);
      if (hasRequest) {
        requests.push(roomId);
      }
    });
    return requests;
  }, [joinRequests, localParticipantId]);

  if (!isOpen) return null;

  const handleRequestJoin = (roomId) => {
    requestJoinRoom(roomId);
  };

  const handleLeaveRoom = () => {
    if (window.confirm("Are you sure you want to leave this breakout room?")) {
      leaveRoom();
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75"
      style={{ 
        zIndex: 99999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-[#060621] rounded-lg shadow-xl border border-gray-600 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Breakout Rooms</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Current Room Status */}
          {currentRoom ? (
            <div className="bg-[#7700ff] bg-opacity-20 border border-[#7700ff] rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-white mb-1">
                    You are in: {rooms[currentRoom]?.name || "Breakout Room"}
                  </h3>
                  <p className="text-sm text-gray-300">
                    {(rooms[currentRoom]?.participants || []).length} participant
                    {(rooms[currentRoom]?.participants || []).length !== 1 ? "s" : ""} in this room
                  </p>
                </div>
                <button
                  onClick={handleLeaveRoom}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Leave Room
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-green-600 bg-opacity-20 border border-green-600 rounded-lg p-4">
              <div>
                <h3 className="text-lg font-medium text-white mb-1">
                  You are in: Main Meeting
                </h3>
                <p className="text-sm text-gray-300">
                  You are in the main meeting room with all participants
                </p>
              </div>
            </div>
          )}

          {/* Available Rooms */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4">
              {currentRoom ? "Available Rooms" : "Join a Breakout Room"}
            </h3>
            {roomList.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No breakout rooms available
              </div>
            ) : (
              <div className="space-y-3">
                {roomList.map((room) => {
                  const isInRoom = currentRoom === room.id;
                  const hasPendingRequest = pendingRequests.includes(room.id);
                  const participantCount = (room.participants || []).length;

                  return (
                    <div
                      key={room.id}
                      className={`bg-gray-800 rounded-lg p-4 border ${
                        isInRoom ? "border-[#7700ff]" : "border-gray-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-base font-medium text-white">{room.name}</h4>
                            {isInRoom && (
                              <span className="px-2 py-1 bg-[#7700ff] text-white rounded text-xs">
                                Current Room
                              </span>
                            )}
                            {hasPendingRequest && (
                              <span className="px-2 py-1 bg-yellow-600 text-white rounded text-xs">
                                Request Pending
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">
                            {participantCount} participant{participantCount !== 1 ? "s" : ""}
                          </p>
                        </div>
                        {!isInRoom && (
                          <button
                            onClick={() => handleRequestJoin(room.id)}
                            disabled={hasPendingRequest}
                            className={`px-4 py-2 rounded text-sm transition-colors ${
                              hasPendingRequest
                                ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                                : "bg-[#7700ff] text-white hover:bg-[#6600dd]"
                            }`}
                          >
                            {hasPendingRequest ? "Request Sent" : "Request to Join"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
